import { Button, DropdownMenu, Box, Flex, Tooltip, Text, TextField } from '@radix-ui/themes';
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import { FolderIcon, HelpOutlineIcon } from 'shared/icons';
import { useEffect, useState } from 'react';
import { cx } from 'class-variance-authority';

import { FormField } from 'shared/components';
import { FavoritesStore } from './FavoritesStore';

import './add-to-favorites.scss';
import { AddToFavoriteDto, FavFolder } from './types';
import { observer } from 'mobx-react-lite';
import { toast } from 'sonner';

function FavoritesCvsView({
   store,
   candidateId,
   cvId,
   favoriteInfo,
   refetch,
   disabled = false,
}: {
   store: FavoritesStore;
   favoriteInfo: Omit<AddToFavoriteDto, 'selectedFolderId'>;
   candidateId: number;
   cvId: number;
   disabled: boolean;
   refetch: () => void;
}) {
   const [showCreateFolderInput, setShowCreateFolderInput] = useState(false);
   const [selectedFolder, setSelectedFolder] = useState<FavFolder | null>(null);

   useEffect(() => {
      store.loadFavFolderList({});
   }, []);

   return (
      <DropdownMenu.Root>
         <DropdownMenu.Trigger disabled={disabled}>
            <Button size="4" radius="small">
               <StarOutlineOutlinedIcon />
               Додати до обраних
            </Button>
         </DropdownMenu.Trigger>

         <DropdownMenu.Content className="add-to-favorites-dropdown">
            <Box className="add-to-favorites-dropdown__content">
               <Flex gap="1" direction="column" mb="5">
                  {store.favFolders.map((favFolder) => {
                     return (
                        <div
                           key={favFolder.id}
                           className={cx(
                              'fav-folder',
                              selectedFolder?.id === favFolder.id && 'fav-folder--selected'
                           )}
                           onClick={() => {
                              setSelectedFolder(favFolder);
                           }}
                        >
                           {favFolder.name}
                           <span>
                              <FolderIcon color="#84a1ce93" />
                           </span>
                        </div>
                     );
                  })}
               </Flex>

               <Flex gap="1" mb="5">
                  <Button
                     size="3"
                     color="blue"
                     loading={store.addToFavoriteState.status === 'pending'}
                     disabled={!selectedFolder}
                     onClick={() => {
                        store.addToFavorite(
                           { ...favoriteInfo, selectedFolderId: selectedFolder?.id },
                           { candidateId, cvId },
                           {
                              onSuccess: () => {
                                 toast.success('Додано до обраних успішно');
                                 refetch?.();
                              },
                              onError: () => {
                                 toast.success('Щось пійшло не так, спробуйте пізніше');
                              },
                           }
                        );
                     }}
                  >
                     Додати до обраних
                  </Button>

                  {!selectedFolder && (
                     <Tooltip content="Щоб додати оберіть папку">
                        <Text color="gray">
                           <HelpOutlineIcon width={18} height={18} />
                        </Text>
                     </Tooltip>
                  )}
               </Flex>

               <DropdownMenu.Separator />
               <Flex pt="3" width="100%" direction="column" align="start" gap="3">
                  {showCreateFolderInput && (
                     <FormField label="">
                        <TextField.Root
                           value={store.folderName}
                           onChange={(e) => {
                              store.setFolderName(e.target.value);
                           }}
                           placeholder="Назва папки"
                        />
                     </FormField>
                  )}

                  <Button
                     ml="2"
                     variant="ghost"
                     size="2"
                     disabled={!store.folderName && showCreateFolderInput}
                     loading={store.createFolderState.status === 'pending'}
                     onClick={() => {
                        if (showCreateFolderInput) {
                           store.createFolder({
                              onSuccess: () => {
                                 setShowCreateFolderInput(false);
                              },
                           });
                        } else {
                           setShowCreateFolderInput(true);
                        }
                     }}
                  >
                     {!showCreateFolderInput && <CreateNewFolderIcon />}
                     Створити папку
                  </Button>
               </Flex>
            </Box>
         </DropdownMenu.Content>
      </DropdownMenu.Root>
   );
}

export const AddToFavorites = observer(FavoritesCvsView);
